import { useState, useEffect, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import { db, storage } from './firebase';
import { collection, addDoc, query, onSnapshot, doc, deleteDoc, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth } from './firebase';
import DeleteConfirmModal from './components/DeleteConfirmModal';


function Home({user}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(false);
  const [winnerCount, setWinnerCount] = useState(1);
  const [description, setDescription] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [gameType, setGameType] = useState('rps');
  const [myEvents, setMyEvents] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetEvent, setDeleteTargetEvent] = useState(null);
  const navigate = useNavigate();

  // 내 이벤트 불러오기
  useEffect(() => { 
    if (!user?.uid) return;

    let unsubscribe = null;
    const myEventsQuery = query(
      collection(db, 'events'),
      where('creatorId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    unsubscribe = onSnapshot(myEventsQuery, (snapshot) => {
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        participants: 0
      }));
      setMyEvents(events);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid]);

  // 이미지 선택
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const createEvent = async () => {
  if (!eventName.trim()) return alert(t('alerts.enterEventName'));
  if (!description.trim()) return alert(t('alerts.enterDescription'));
  // 이미지 필수 체크 제거됨

  setLoading(true);

  try {
    let imageUrl = '';

    // 이미지가 있을 때만 업로드
    if (imageFile) {
      const imageRef = ref(storage, `events/${Date.now()}_${imageFile.name}`);
      await uploadBytes(imageRef, imageFile);
      imageUrl = await getDownloadURL(imageRef);
    }

    const docRef = await addDoc(collection(db, 'events'), {
      name: eventName,
      description: description,
      deliveryMethod: deliveryMethod,
      imageUrl: imageUrl, // 이미지 없으면 빈 문자열로 저장
      createdAt: new Date(),
      completed: false,
      winnerCount: winnerCount,
      gameType: gameType,
      creatorId: auth.currentUser.uid
    });

    setEventName('');
    setDescription('');
    setDeliveryMethod('');
    setImageFile(null);
    setImagePreview(null);
    navigate(`/event/${docRef.id}`);
  } catch (error) {
    console.error('이벤트 생성 실패:', error);
    alert(t('alerts.createEventFailed'));
    setLoading(false);
  }
};
 

  const deleteEvent = async (eventId, event) => {
    if (event.creatorId !== auth.currentUser?.uid) {
      alert(t('alerts.onlyCreatorCanDelete'));
      return;
    }
    setDeleteTargetEvent({ id: eventId, name: event.name });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetEvent) return;
    try {
      await deleteDoc(doc(db, 'events', deleteTargetEvent.id));
      setShowDeleteModal(false);
      setDeleteTargetEvent(null);
    } catch (error) {
      console.error('삭제 실패:', error);
      alert(t('alerts.deleteFailed'));
    }
  };

  return (
    <div style={{ 
      //minHeight: '100vh',
      background: '#F4F5F7', // 카드들이 돋보이도록 배경색을 아주 옅은 회색으로 변경
      fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
      paddingBottom: '40px'
    }}>
      <Header />
      
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 15px' }}>
        
        {/* 이벤트 만들기 카드 (PickFlip 스타일 적용) */}
        <div id="create-event-section" style={{
          backgroundColor: '#FFFFFF',
          padding: '30px',
          borderRadius: '16px',
          marginBottom: '40px',
          border: '4px solid #2C3E50',
          boxShadow: '8px 8px 0px #2C3E50', // 강렬한 브루탈리즘 그림자
        }}>
          <h2 style={{ 
            marginBottom: '20px',
            fontSize: '24px',
            color: '#2C3E50',
            fontWeight: '900',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>✨</span> {t('home.title')}
          </h2>

          <input
            type="text"
            placeholder={t('home.eventName')}
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && createEvent()}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '18px',
              border: '3px solid #2C3E50',
              borderRadius: '12px',
              marginBottom: '25px',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              backgroundColor: '#FFFFFF',
              fontWeight: '600'
            }}
          />

          {/* 게임 타입 선택 */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '10px',
              fontSize: '14px',
              fontWeight: '800',
              color: '#7F8C8D',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {t('home.gameType')}
            </label>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
              {/* 공통 버튼 스타일 추출을 통한 간결화 및 효과 강화 */}
              {[
                { type: 'rps', icon: '✊', label: t('home.rps'), color: '#FF6B9D' },
                { type: 'tarot', icon: '🔮', label: t('home.tarot'), color: '#A3D9FF' },
                { type: 'pets', icon: '🐕', label: t('home.pets'), color: '#FFD93D' }
              ].map((game) => (
                <button
                  key={game.type}
                  type="button"
                  onClick={() => setGameType(game.type)}
                  style={{
                    padding: '20px 10px',
                    fontSize: '15px',
                    backgroundColor: gameType === game.type ? game.color : '#FFFFFF',
                    color: gameType === game.type && game.type === 'rps' ? '#FFFFFF' : '#2C3E50',
                    border: '3px solid #2C3E50',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: '800',
                    transition: 'all 0.2s',
                    fontFamily: 'inherit',
                    boxShadow: gameType === game.type ? '4px 4px 0px #2C3E50' : 'none',
                    transform: gameType === game.type ? 'translate(-2px, -2px)' : 'none',
                    opacity: gameType !== game.type && gameType !== '' ? 0.7 : 1
                  }}
                  onMouseOver={(e) => { if (gameType !== game.type) e.currentTarget.style.transform = 'translate(-2px, -2px)'; }}
                  onMouseOut={(e) => { if (gameType !== game.type) e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>{game.icon}</div>
                  <div>{game.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 당첨자 수 */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '800', color: '#7F8C8D', letterSpacing: '1px' }}>
              {t('home.winnerCount')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number" min="1" max="100" value={winnerCount}
                onClick={(e) => e.target.select()} onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') { setWinnerCount(''); return; }
                  const num = parseInt(value);
                  if (num >= 1 && num <= 100) setWinnerCount(num);
                }}
                onBlur={(e) => { if (e.target.value === '' || parseInt(e.target.value) < 1) setWinnerCount(1); }}
                style={{
                  width: '100%', padding: '16px', fontSize: '18px', border: '3px solid #2C3E50',
                  borderRadius: '12px', boxSizing: 'border-box', backgroundColor: '#FFFFFF',
                  fontWeight: '700', fontFamily: 'inherit'
                }}
              />
              <div style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#7F8C8D', fontWeight: '700', pointerEvents: 'none' }}>
                {t('event.winnerCount', { count: '' })}
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#95A5A6', marginTop: '8px', fontWeight: '600' }}>
              {t('home.winnerCountHint')}
            </div>
          </div>

          {/* 상품 이미지 */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '800', color: '#7F8C8D', letterSpacing: '1px' }}>
              {t('home.productImage')}
            </label>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
            <button
              type="button" onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%', padding: '16px', fontSize: '16px', backgroundColor: '#FFF5E6', color: '#2C3E50',
                border: '3px dashed #2C3E50', borderRadius: '12px', cursor: 'pointer', fontWeight: '700',
                fontFamily: 'inherit', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {imagePreview ? imageFile?.name : t('home.chooseFile')}
              </span>
              <span style={{ fontSize: '24px', color: imagePreview ? '#6BCB77' : '#2C3E50' }}>
                {imagePreview ? '✅' : '📸'}
              </span>
            </button>

             {/* 👇 여기 추가 */}
  <p style={{
    fontSize: '12px',
    color: '#7F8C8D',
    marginTop: '8px',
    marginBottom: '0'
  }}>
    💡 {t('home.imageGuide')}
  </p>
            
            {imagePreview && (
              <div style={{ marginTop: '15px', border: '3px solid #2C3E50', borderRadius: '12px', padding: '10px', backgroundColor: '#FFFFFF', textAlign: 'center' }}>
                <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', objectFit: 'contain' }} />
              </div>
            )}
          </div>

          {/* 상품 설명 */}
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '800', color: '#7F8C8D', letterSpacing: '1px' }}>
              {t('home.productDescription')}
            </label>
            <textarea
              placeholder={t('home.productDescriptionPlaceholder')} value={description} onChange={(e) => setDescription(e.target.value)}
              style={{ width: '100%', padding: '16px', fontSize: '16px', border: '3px solid #2C3E50', borderRadius: '12px', boxSizing: 'border-box', minHeight: '120px', resize: 'vertical', fontFamily: 'inherit', backgroundColor: '#FFFFFF', fontWeight: '500' }}
            />
          </div>

          {/* 수령 방법 */}
          <div style={{ marginBottom: '30px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '800', color: '#7F8C8D', letterSpacing: '1px' }}>
              {t('home.deliveryMethod')}
            </label>
            <textarea
              placeholder={t('home.deliveryMethodPlaceholder')} value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}
              style={{ width: '100%', padding: '16px', fontSize: '16px', border: '3px solid #2C3E50', borderRadius: '12px', boxSizing: 'border-box', minHeight: '80px', resize: 'vertical', fontFamily: 'inherit', backgroundColor: '#FFFFFF', fontWeight: '500' }}
            />
          </div>

          {/* 생성 버튼 */}
          <button
            onClick={createEvent} disabled={loading}
            style={{
              width: '100%', padding: '20px', fontSize: '20px', backgroundColor: loading ? '#95A5A6' : '#6BCB77',
              color: '#FFFFFF', border: '4px solid #2C3E50', borderRadius: '12px', cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '900', fontFamily: 'inherit', transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '4px 4px 0px #2C3E50', transform: loading ? 'none' : 'translate(-2px, -2px)'
            }}
            onMouseOver={(e) => { if (!loading) { e.currentTarget.style.transform = 'translate(-4px, -4px)'; e.currentTarget.style.boxShadow = '6px 6px 0px #2C3E50'; } }}
            onMouseOut={(e) => { if (!loading) { e.currentTarget.style.transform = 'translate(-2px, -2px)'; e.currentTarget.style.boxShadow = '4px 4px 0px #2C3E50'; } }}
          >
            {loading ? t('home.creating') : t('home.title')}
          </button>
        </div>

        {/* 내 이벤트 목록 */}
        {myEvents.length > 0 && (
          <div>
            <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#2C3E50', fontWeight: '900' }}>
              {t('home.myEvents')}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
              {myEvents.map(event => (
                <div
                  key={event.id}
                  style={{
                    backgroundColor: '#FFFFFF', padding: '25px', borderRadius: '16px', border: '3px solid #2C3E50',
                    position: 'relative', transition: 'all 0.2s', cursor: 'pointer', boxShadow: '4px 4px 0px #2C3E50'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translate(-4px, -4px)'; e.currentTarget.style.boxShadow = '8px 8px 0px #2C3E50'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translate(0, 0)'; e.currentTarget.style.boxShadow = '4px 4px 0px #2C3E50'; }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteEvent(event.id, event); }}
                    style={{
                      position: 'absolute', top: '-10px', right: '-10px', backgroundColor: '#FF6B9D', color: '#FFFFFF',
                      border: '3px solid #2C3E50', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer',
                      fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                      fontWeight: '900', transition: 'all 0.2s', boxShadow: '2px 2px 0px #2C3E50'
                    }}
                    title={t('delete')}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    ×
                  </button>

                  <div onClick={() => navigate(`/event/${event.id}`)}>
                    <div style={{ marginBottom: '15px', paddingRight: '30px' }}>
                      <h3 style={{ margin: 0, fontSize: '20px', color: '#2C3E50', fontWeight: '800', marginBottom: '10px' }}>
                        {event.name}
                      </h3>
                      {event.completed && (
                        <span style={{ backgroundColor: '#A3D9FF', color: '#2C3E50', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: '800', border: '2px solid #2C3E50' }}>
                          {t('completed')} ✅
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '15px', fontSize: '14px', fontWeight: '700' }}>
                      <span style={{ color: '#2C3E50', backgroundColor: '#FFF5E6', padding: '8px 12px', borderRadius: '8px', border: '2px solid #2C3E50' }}>
                        👥 {event.participants || 0}{t('event.winnerCount', { count: '' })}
                      </span>
                      <span style={{ color: '#7F8C8D', padding: '8px 0' }}>
                        📅 {event.createdAt?.toDate ? new Date(event.createdAt.toDate()).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {myEvents.length === 0 && (
          <div style={{ padding: '60px 20px', backgroundColor: '#FFFFFF', borderRadius: '16px', border: '4px dashed #BDC3C7', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
            <p style={{ margin: 0, fontSize: '18px', color: '#7F8C8D', fontWeight: '700', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
              {t('home.noEvents')}
            </p>
          </div>
        )}

        <div style={{ marginTop: '50px', textAlign: 'center', color: '#95A5A6', fontSize: '14px', fontWeight: '600' }}>
          <p style={{ margin: '5px 0', whiteSpace: 'pre-line' }}>{t('home.shareInfo')}</p>
        </div>

        <DeleteConfirmModal
          isOpen={showDeleteModal} onConfirm={confirmDelete}
          onCancel={() => { setShowDeleteModal(false); setDeleteTargetEvent(null); }}
          eventName={deleteTargetEvent?.name}
        />
      </div>
    </div>
  );
}

export default Home;